import { useEffect, useState } from "react";
import Seo from "@/components/Seo";
import LeadForm from "@/components/energy/LeadForm";
import { ToolShell, SelectInput, NumInput, WhatsAppCTA } from "@/components/energy/tool";
import { api } from "@/lib/solarApi";

const SURGE_NOTE = {
  high: "This appliance has a motor or compressor — it can draw 2–3× its running power at start-up. Your inverter must be sized for this surge.",
  medium: "This appliance has a modest start-up surge. A quality inverter handles it comfortably.",
  low: "Resistive or electronic load with minimal surge.",
};

export default function EnergySafety() {
  const [library, setLibrary] = useState([]);
  const [key, setKey] = useState("");
  const [customWatts, setCustomWatts] = useState("");
  const [showLead, setShowLead] = useState(false);

  useEffect(() => {
    api.get("/energy/config").then((r) => setLibrary(r.data.appliances)).catch(() => {});
  }, []);

  const appliance = library.find((a) => a.key === key);
  const watts = appliance ? appliance.watts : Number(customWatts) || 0;
  const surge = appliance ? appliance.surge : watts >= 500 ? 2 : 1;
  const surgeLevel = surge >= 2.5 ? "high" : surge > 1 ? "medium" : "low";
  const minInverter = watts ? Math.max(1, Math.ceil((watts * surge * 1.25) / 100) / 10) : 0;

  return (
    <>
      <Seo title="Appliance & Power Safety Assistant — BNM" description="Understand the power, surge, inverter and protection requirements of any appliance before connecting it." />
      <ToolShell
        testid="safety-tool"
        eyebrow="BNM Appliance & Power Safety Assistant"
        title="Know before you plug in."
        lead="Select an appliance to understand its power needs, start-up surge, and inverter and protection considerations. For anything involving wiring or electrical installation, always use a qualified electrician or BNM technical staff."
      >
        <div className="rounded-lg border border-slate-200 bg-white p-6 sm:p-8 space-y-6">
          <SelectInput label="Appliance" testid="safety-appliance" value={key} onChange={setKey}
            options={[{ value: "", label: "Select an appliance…" }, ...library.map((a) => ({ value: a.key, label: a.name }))]} />
          {!appliance && (
            <NumInput label="Or enter appliance wattage directly" suffix="W" min={0} testid="safety-custom-watts" value={customWatts} onChange={setCustomWatts} />
          )}
        </div>

        {watts > 0 && (
          <div className="mt-8 space-y-6" data-testid="safety-guidance">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-lg border border-slate-200 bg-white p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">Typical power requirement</p>
                <p className="mt-2 font-display text-2xl font-extrabold text-slate-900">{watts} W</p>
                <p className="mt-2 text-sm text-slate-600">Running power when in use.</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">Start-up / surge</p>
                <p className="mt-2 font-display text-2xl font-extrabold text-slate-900">{surge > 1 ? `~${Math.round(watts * surge)} W` : "Minimal"}</p>
                <p className="mt-2 text-sm text-slate-600">{SURGE_NOTE[surgeLevel]}</p>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-6 space-y-3 text-sm leading-relaxed text-slate-600">
              <p><b className="text-slate-900">Inverter considerations:</b> choose an inverter rated at least {minInverter} kW continuous for this appliance alone, always pure sine wave. Add other simultaneous loads on top.</p>
              <p><b className="text-slate-900">Voltage:</b> Sierra Leone uses 230 V / 50 Hz. Confirm your appliance matches before connecting.</p>
              <p><b className="text-slate-900">Surge protection:</b> use a Type II SPD at the distribution board, especially in areas with lightning or unstable grids.</p>
              <p><b className="text-slate-900">Stabilization / AVR:</b> {watts >= 1000 || surgeLevel === "high" ? "recommended where grid voltage fluctuates — motors and compressors are sensitive to low voltage." : "useful for sensitive electronics in areas with unstable voltage."}</p>
              {watts >= 2000 && (
                <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800">
                  This is a heavy load. Professional assessment by BNM technical staff or a qualified electrician is recommended before installation.
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => setShowLead(true)} data-testid="safety-cta"
                className="rounded-md bg-obsidian px-6 py-3.5 font-semibold text-white hover:bg-pine hover:text-volt transition-colors">
                Talk to a BNM Energy Specialist
              </button>
              <WhatsAppCTA message={`Hello BNM. I need power advice for a ${appliance ? appliance.name : `${watts}W appliance`}.`} />
            </div>
            {showLead && <LeadForm type="engineering" title="Talk to a BNM Energy Specialist" context={{ appliance: appliance?.name || `${watts}W` }} defaultMessage={`I need guidance on powering: ${appliance ? appliance.name : `${watts} W appliance`}.`} />}
          </div>
        )}
      </ToolShell>
    </>
  );
}
